import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2, RefreshCw, ExternalLink, X, Layers, Info, ImageIcon, Link2,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api/ad-shelf-share';
const RADAR_API = 'https://api.glowytics.com/api/facebook-ads-library-radar';

/* Sequential blue ramp — magnitude is one hue, light→dark (never a rainbow).
   Heat cells always carry their number, which is what lets the pale steps
   stay legible. */
const RAMP = ['#eef4fd', '#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];
const ACCENT = '#2a78d6';
const INK_FLIP = 4; // ramp index at and above which cell text turns white

const getBrandId = () => {
  try { const c = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId'); if (c) return c; } catch (_) {}
  for (const k of ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id']) { try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {} }
  for (const k of ['selectedBrand', 'currentBrand', 'activeBrand']) { try { const r = localStorage.getItem(k) || sessionStorage.getItem(k); if (r) { const o = JSON.parse(r); if (o?.id) return o.id; } } catch (_) {} }
  return '';
};

const pct = (n) => `%${Number(n || 0).toFixed(1).replace('.', ',')}`;
const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const bucketLabel = (b) => (b?.length === 7 ? `${MONTHS[Number(b.slice(5, 7)) - 1]} ${b.slice(2, 4)}` : b);

function rampStep(share, max) {
  if (!share) return -1;
  const t = max > 0 ? share / max : 0;
  return Math.min(RAMP.length - 1, Math.max(1, Math.round(t * (RAMP.length - 1))));
}

/* Bar with the data end rounded and the baseline end square. */
function barPath(x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h);
  if (h <= 0) return '';
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
}

/* ── Monthly small multiple ──────────────────────────────────────── */
function TrendBars({ points, label, max }) {
  const [hover, setHover] = useState(null);
  if (!points?.length) return <div className="h-[64px] flex items-center text-[11px] text-slate-400">Geçmiş yok</div>;

  // One scale across every panel — per-panel scaling would make a 4% shelf
  // look identical to a 34% one.
  const W = 30, H = 52, PAD = 6;
  const width = points.length * W;

  return (
    <div className="relative">
      <svg width={width} height={H + 16} className="overflow-visible" role="img"
        aria-label={`${label} aylık pay`}>
        <line x1="0" y1={H} x2={width} y2={H} stroke="#e2e8f0" strokeWidth="1" />
        {points.map((p, i) => {
          const h = Math.round(((p.share / max) * (H - PAD)) || 0);
          const x = i * W + 6;
          const on = hover === i;
          return (
            <g key={p.bucket}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={i * W} y="0" width={W} height={H + 16} fill="transparent" />
              {h > 0 && <path d={barPath(x, H - h, W - 12, h, 4)} fill={on ? '#184f95' : ACCENT} />}
              {h === 0 && <line x1={x} y1={H} x2={x + W - 12} y2={H} stroke="#cbd5e1" strokeWidth="2" />}
              <text x={x + (W - 12) / 2} y={H + 12} textAnchor="middle"
                className="fill-slate-400" style={{ fontSize: 9 }}>
                {bucketLabel(p.bucket).split(' ')[0]}
              </text>
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <div className="absolute z-20 -top-1 left-0 pointer-events-none"
          style={{ transform: `translate(${Math.max(0, hover * W - 30)}px, -100%)` }}>
          <div className="rounded-lg bg-slate-900 text-white px-2.5 py-1.5 shadow-lg whitespace-nowrap">
            <div className="text-[11px] font-semibold">{bucketLabel(points[hover].bucket)}</div>
            <div className="text-[10px] text-slate-300">
              {points[hover].brand_ads}/{points[hover].total_ads} reklam · {pct(points[hover].share)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Creative drawer ─────────────────────────────────────────────── */
function CreativeModal({ page, brandLabel, date, onClose }) {
  const [ads, setAds] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = new URLSearchParams({ pageDbId: page.id, brand: page.brandKey, ...(date ? { date } : {}) });
        const r = await fetch(`${API}/ads?${qs}`, { headers: { 'X-Brand-ID': getBrandId() }, credentials: 'include' });
        const j = await r.json();
        if (!alive) return;
        if (!r.ok) { setErr(j?.error || 'Yüklenemedi'); return; }
        setAds(j.ads || []);
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, [page, date]);

  const img = (adId, idx) =>
    `${RADAR_API}/media-proxy?${new URLSearchParams({ ad: adId, page: page.id, kind: 'image', idx: String(idx) })}`;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{page.page_name} · {brandLabel}</h3>
            <p className="text-xs text-slate-500">
              {date ? `${date} anlık görüntüsü` : 'Son anlık görüntü'} · eşleşen reklamlar
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {err && <div className="text-sm text-rose-600">{err}</div>}
          {!ads && !err && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…
            </div>
          )}
          {ads?.length === 0 && <div className="text-sm text-slate-500 py-8 text-center">Bu günde eşleşen reklam yok.</div>}

          <div className="space-y-4">
            {ads?.map(ad => (
              <article key={ad.ad_archive_id} className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-900 truncate">{ad.snapshot_title || '(başlıksız)'}</div>
                    <div className="text-[11px] text-slate-500">
                      {ad.start_date ? `Başlangıç ${String(ad.start_date).slice(0, 10)}` : '—'}
                      {' · '}{ad.matched_cards.length}/{ad.total_cards || 1} kart eşleşti
                      {ad.publisher_platform ? ` · ${ad.publisher_platform.replace(/,/g, ', ')}` : ''}
                    </div>
                  </div>
                  {ad.ad_url && (
                    <a href={ad.ad_url} target="_blank" rel="noreferrer"
                      className="shrink-0 text-slate-400 hover:text-[#0668E1]" title="Ads Library'de aç">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200/60">
                  {ad.matched_cards.map(c => (
                    <div key={c.idx} className="bg-white p-2">
                      <div className="aspect-square rounded-lg bg-slate-100 overflow-hidden mb-1.5">
                        <img src={img(ad.ad_archive_id, c.idx)} alt="" loading="lazy"
                          referrerPolicy="no-referrer" className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      </div>
                      <div className="text-[11px] font-medium text-slate-800 line-clamp-2 leading-snug">
                        {c.title || '(başlıksız kart)'}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600">
                          {c.signal}
                        </span>
                        {c.link_url && (
                          <a href={c.link_url} target="_blank" rel="noreferrer"
                            className="text-[10px] text-slate-400 hover:text-[#0668E1] truncate" title={c.link_url}>
                            <Link2 className="w-3 h-3 inline" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */
export default function AdShelfShare() {
  const [brands, setBrands] = useState([]);
  const [brandKey, setBrandKey] = useState('on');
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);

  const brandLabel = brands.find(b => b.key === brandKey)?.label || brandKey;

  const load = useCallback(async () => {
    const brandId = getBrandId();
    if (!brandId) { toast.error('Marka seçili değil'); setLoading(false); return; }
    setLoading(true);
    try {
      const [bRes, oRes] = await Promise.all([
        fetch(`${API}/brands`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
        fetch(`${API}/overview`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
      ]);
      const bJson = await bRes.json();
      const oJson = await oRes.json();
      if (bRes.ok) setBrands(bJson.brands || []);
      if (!oRes.ok) throw new Error(oJson?.error || 'Özet yüklenemedi');
      setOverview(oJson);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const brandId = getBrandId();
    if (!brandId || !brandKey) return;
    let alive = true;
    setTrendLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/trend?brand=${encodeURIComponent(brandKey)}&granularity=month`,
          { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
        const j = await r.json();
        if (alive && r.ok) setTrend(j);
      } catch (_) { /* trend is a nice-to-have */ }
      finally { if (alive) setTrendLoading(false); }
    })();
    return () => { alive = false; };
  }, [brandKey]);

  const pages = useMemo(
    () => (overview?.pages || []).slice().sort((a, b) => b.total_ads - a.total_ads),
    [overview]
  );

  const selected = useMemo(() => {
    const m = new Map();
    for (const p of pages) {
      const hit = p.brands.find(b => b.key === brandKey);
      m.set(p.id, hit || { ads: 0, cards: 0, share: 0 });
    }
    return m;
  }, [pages, brandKey]);

  const trendByPage = useMemo(() => {
    const m = new Map();
    for (const s of (trend?.series || [])) m.set(s.id, s.points);
    return m;
  }, [trend]);

  const trendMax = useMemo(
    () => Math.max(1, ...(trend?.series || []).flatMap(s => s.points.map(p => p.share))),
    [trend]
  );

  // Shelf composition matrix: the brands that actually fill these shelves.
  const matrix = useMemo(() => {
    const totals = new Map();
    for (const p of pages) for (const b of p.brands) totals.set(b.key, (totals.get(b.key) || 0) + b.ads);
    const cols = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10).map(([k]) => k);
    const labelOf = (k) => brands.find(b => b.key === k)?.label || k;
    const rows = pages.map(p => ({
      page: p,
      cells: cols.map(k => p.brands.find(b => b.key === k)?.share || 0),
    }));
    const max = Math.max(1, ...rows.flatMap(r => r.cells));
    return { cols, labelOf, rows, max };
  }, [pages, brands]);

  const totalAds = pages.reduce((s, p) => s + p.total_ads, 0);
  const totalBrandAds = pages.reduce((s, p) => s + (selected.get(p.id)?.ads || 0), 0);

  return (
    <div className="p-6 bg-slate-50/50 min-h-screen">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2.5 mb-1">
            <Layers className="w-5 h-5 text-slate-700" />
            <h1 className="text-xl font-semibold text-slate-900">Ad Shelf Share</h1>
          </div>
          <p className="text-sm text-slate-600 max-w-3xl">
            Takip ettiğimiz Meta Ads Library sayfalarının reklam rafında hangi ürün markasının ne kadar
            yer kapladığı. Eşleştirme <strong>reklam metninde değil, carousel kartlarının ürün URL'i ve
            başlığında</strong> yapılır — bir reklam 10 farklı markanın ürününü taşıyabildiği için.
          </p>
        </div>

        {/* Toolbar */}
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-3.5 mb-5 flex flex-wrap items-center gap-3">
          <label className="text-xs font-medium text-slate-500">Marka</label>
          <select value={brandKey} onChange={e => setBrandKey(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-slate-300 outline-none min-w-[170px]">
            {brands.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>

          {overview?.date && (
            <span className="text-xs text-slate-500 px-2 py-1 rounded-md bg-slate-100">
              anlık görüntü {overview.date}
            </span>
          )}

          <div className="ml-auto flex items-center gap-3">
            {totalAds > 0 && (
              <span className="text-xs text-slate-600">
                <strong className="text-slate-900">{totalBrandAds}</strong> / {totalAds} reklam
                {' · '}{pct(totalAds ? (totalBrandAds / totalAds) * 100 : 0)}
              </span>
            )}
            <button onClick={load} disabled={loading}
              className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Yenile
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 text-slate-500 py-20">
            <Loader2 className="w-5 h-5 animate-spin" /> Raf hesaplanıyor…
          </div>
        )}

        {!loading && !pages.length && (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
            Bu markaya bağlı Ads Library sayfası yok. Önce Facebook Ads Library sayfasından rakip ekleyin.
          </div>
        )}

        {/* Per-page cards */}
        {!loading && !!pages.length && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {pages.map(p => {
              const s = selected.get(p.id);
              const unmatched = p.total_ads - (p.matched_ads || 0);
              return (
                <article key={p.id}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-slate-300 transition-colors">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl border border-slate-200 bg-slate-100 overflow-hidden shrink-0">
                      {p.page_image
                        ? <img src={p.page_image} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-400" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{p.page_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {p.ig_username ? `@${p.ig_username} · ` : ''}{p.total_ads} aktif reklam
                        {unmatched > 0 ? ` · ${unmatched} markasız` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="px-4 py-4 flex items-start gap-5">
                    <div className="shrink-0">
                      <div className="text-3xl font-semibold tabular-nums" style={{ color: s.ads ? '#0f172a' : '#94a3b8' }}>
                        {pct(s.share)}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {s.ads} reklam · {s.cards} ürün kartı
                      </div>
                      <div className="mt-2 h-2 w-[132px] rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, s.share)}%`, background: ACCENT }} />
                      </div>
                      {s.ads > 0 && (
                        <button onClick={() => setModal({ ...p, brandKey })}
                          className="mt-2.5 text-[11px] font-medium text-[#2a78d6] hover:underline">
                          Kreatifleri gör →
                        </button>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 overflow-x-auto">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                        {brandLabel} payı · aylık <span className="normal-case tracking-normal text-slate-300">(0–{pct(trendMax)} ortak ölçek)</span>
                      </div>
                      {trendLoading
                        ? <div className="h-[64px] flex items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
                        : <TrendBars points={trendByPage.get(p.id)} max={trendMax} label={`${p.page_name} ${brandLabel}`} />}
                    </div>
                  </div>

                  {/* Shelf top brands */}
                  <div className="px-4 pb-3.5 flex flex-wrap gap-1.5">
                    {(() => {
                      // Keep the selected brand visible even when it isn't in
                      // the shelf's top 7 — otherwise the hero reads %0,0 with
                      // no chip to explain where it sits.
                      const top = p.brands.slice(0, 7);
                      const sel = p.brands.find(b => b.key === brandKey);
                      return sel && !top.some(b => b.key === brandKey) ? [sel, ...top.slice(0, 6)] : top;
                    })().map(b => (
                      <span key={b.key}
                        className={`text-[11px] rounded-md px-1.5 py-0.5 border ${b.key === brandKey
                          ? 'bg-[#2a78d6] text-white border-[#2a78d6] font-semibold'
                          : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {matrix.labelOf(b.key)} {pct(b.share)}
                      </span>
                    ))}
                    {!p.brands.length && <span className="text-[11px] text-slate-400">Marka eşleşmesi yok</span>}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Composition matrix */}
        {!loading && !!matrix.cols.length && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-6">
            <div className="px-5 py-3.5 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Raf kompozisyonu</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Her hücre: o sayfanın o günkü reklamlarının yüzde kaçında o markanın ürünü var.
                Bir reklam birden fazla marka taşıyabilir, satır toplamı %100 olmak zorunda değil.
              </p>
            </div>
            <div className="overflow-x-auto relative">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Sayfa</th>
                    <th className="text-right px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Reklam</th>
                    {matrix.cols.map(k => (
                      <th key={k} className="px-2 py-2.5 text-[11px] font-semibold text-slate-600 whitespace-nowrap">
                        {matrix.labelOf(k)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map(({ page, cells }) => (
                    <tr key={page.id} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-2 text-slate-800 font-medium whitespace-nowrap">{page.page_name}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-500">{page.total_ads}</td>
                      {cells.map((v, i) => {
                        const step = rampStep(v, matrix.max);
                        const key = `${page.id}:${i}`;
                        return (
                          <td key={matrix.cols[i]} className="px-1 py-1 text-center relative"
                            onMouseEnter={() => setHoverCell(key)} onMouseLeave={() => setHoverCell(null)}>
                            <div className="rounded-md py-1.5 text-[11px] font-semibold tabular-nums"
                              style={{
                                background: step < 0 ? '#f8fafc' : RAMP[step],
                                color: step >= INK_FLIP ? '#ffffff' : (step < 0 ? '#cbd5e1' : '#0f172a'),
                                outline: matrix.cols[i] === brandKey ? '2px solid #0f172a' : 'none',
                                outlineOffset: '-2px',
                              }}>
                              {v ? pct(v) : '·'}
                            </div>
                            {hoverCell === key && v > 0 && (
                              <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1 pointer-events-none">
                                <div className="rounded-lg bg-slate-900 text-white px-2.5 py-1.5 shadow-lg whitespace-nowrap text-left">
                                  <div className="text-[11px] font-semibold">{matrix.labelOf(matrix.cols[i])}</div>
                                  <div className="text-[10px] text-slate-300">
                                    {page.brands.find(b => b.key === matrix.cols[i])?.ads} / {page.total_ads} reklam
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Method / limits */}
        {!loading && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-xs text-amber-900 leading-relaxed">
            <div className="flex items-center gap-1.5 font-semibold mb-1.5">
              <Info className="w-3.5 h-3.5" /> Yöntem ve sınırlar
            </div>
            <ul className="list-disc pl-4 space-y-1">
              <li>
                Marka üç sinyalle atanır: ürün URL'indeki marka bölümü (<code>url</code>), kart başlığının
                marka ile başlaması (<code>title</code>), markaya özgü model adı (<code>model</code>).
                Kreatif penceresinde her kartın hangi sinyalle eşleştiği yazar.
              </li>
              <li>
                "On" için düz metin araması bilerek kullanılmaz: İngilizce edat ve Türkçe "on" (10) yüzünden
                <em> Carry On</em>, <em>Slip-On</em>, <em>Always On</em>, <em>ON Dijital</em> gibi eşleşmeler üretir.
                Bunlar kara listede.
              </li>
              <li>
                Sayılar Meta Ads Library'nin o günkü anlık görüntüsünden gelir; harcama ve erişim
                Meta tarafından ticari reklamlarda paylaşılmaz, o yüzden burada bütçe değil <strong>raf payı</strong> ölçülür.
              </li>
              <li>
                Aylık seri, o ay içinde en az bir kez gördüğümüz reklamların payıdır. 25 Mart 2026 öncesi veri yok.
              </li>
            </ul>
          </div>
        )}
      </div>

      {modal && (
        <CreativeModal page={modal} brandLabel={brandLabel} date={overview?.date}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}
